import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useGetAdminGroupsQuery,
    useGetProductsQuery,
    useReorderGroupsMutation,
    useReorderProductsMutation,
} from '../../api/group/groupAdminApiSlice';
import { useReorderProductCategoriesMutation } from '../../api/productCategory/productCategoryApiSlice';
import { useTenantProductCategoriesQuery } from '../../hooks/useTenantProductCategoriesQuery';
import { sortByManualOrder } from '../../utils/manualOrder';
import ManualOrderSection, { OrderScope } from './ManualOrderSection';

interface Props {
    readOnly: boolean;
    /** Operational tenant: product categories (and so their products) exist. */
    isSelfService: boolean;
}

/**
 * The order the booking page shows things in (#3016) — three units, each
 * arranged on its own:
 *
 *   - product categories: the home tiles of one use case (the standard
 *     category stays first, as on the home screen — ADR-0026 §AE);
 *   - products within one category;
 *   - rooms/objects (Group, the product-location cell, ADR-0025) at one
 *     location — the order of the end-user lists and of the admin lists.
 *
 * A scope is offered only when it has at least two rows to arrange. Each unit
 * sends the COMPLETE list of its scope; the backend refuses a stale one.
 */
const CatalogueOrderSections = ({ readOnly, isSelfService }: Props) => {
    const { t } = useTranslation();
    const categoriesQuery = useTenantProductCategoriesQuery({ includeArchived: true });
    const productsQuery = useGetProductsQuery(undefined, { skip: !isSelfService });
    const groupsQuery = useGetAdminGroupsQuery();
    const [reorderCategories] = useReorderProductCategoriesMutation();
    const [reorderProducts] = useReorderProductsMutation();
    const [reorderGroups] = useReorderGroupsMutation();
    const missing = String(t('common.missing'));

    const liveCategories = useMemo(
        () => (categoriesQuery.data ?? []).filter((category) => !category.archivedAt),
        [categoriesQuery.data],
    );

    const categoryScopes = useMemo<OrderScope[]>(() => {
        const useCases = Array.from(new Set(liveCategories.map((c) => c.useCase.toLowerCase())));
        return useCases
            .map((useCase) => {
                const ofUseCase = liveCategories.filter((c) => c.useCase.toLowerCase() === useCase);
                const further = sortByManualOrder(ofUseCase.filter((c) => !c.isStandard), (c) => c.name);
                return {
                    value: useCase,
                    label: t(`service_type_selector.${useCase}`, { defaultValue: useCase }),
                    items: further.map((c) => ({ id: c.id, label: c.name })),
                    pinned: ofUseCase.filter((c) => c.isStandard).map((c) => ({
                        id: c.id,
                        label: c.name,
                        hint: t('services.manual_order.categories.standard_pinned'),
                    })),
                };
            })
            .filter((scope) => scope.items.length >= 2);
    }, [liveCategories, t]);

    const productScopes = useMemo<OrderScope[]>(() => {
        const products = productsQuery.data ?? [];
        return sortByManualOrder(liveCategories, (c) => c.name)
            .map((category) => {
                const useCaseKey = 'service_type_selector.' + category.useCase.toLowerCase();
                const useCaseLabel = t(useCaseKey, { defaultValue: category.useCase });
                return {
                    value: category.id,
                    label: `${category.name} (${useCaseLabel})`,
                    items: sortByManualOrder(
                        products.filter((p) => p.productCategoryId === category.id),
                        (p) => p.name ?? '',
                    ).map((p) => ({ id: p.id, label: p.name ?? missing })),
                };
            })
            .filter((scope) => scope.items.length >= 2);
    }, [liveCategories, productsQuery.data, t, missing]);

    const groupScopes = useMemo<OrderScope[]>(() => {
        const groups = (groupsQuery.data ?? []).filter((g) => g.locationId);
        const locationIds = Array.from(new Set(groups.map((g) => g.locationId as string)));
        return locationIds
            .map((locationId) => {
                const atLocation = groups.filter((g) => g.locationId === locationId);
                return {
                    value: locationId,
                    label: atLocation[0]?.locationName ?? missing,
                    items: sortByManualOrder(atLocation, (g) => g.name ?? '')
                        .map((g) => ({ id: g.id, label: g.name ?? missing })),
                };
            })
            .filter((scope) => scope.items.length >= 2)
            .sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }));
    }, [groupsQuery.data, missing]);

    return (
        <div id={'manual-order'} className={'flex flex-col gap-6'}>
            {isSelfService && (
                <ManualOrderSection
                    id={'manual-order-categories'}
                    title={t('services.manual_order.categories.title')}
                    description={t('services.manual_order.categories.description')}
                    scopeLabel={t('services.manual_order.categories.scope')}
                    scopes={categoryScopes}
                    readOnly={readOnly}
                    isLoading={categoriesQuery.isLoading}
                    isError={categoriesQuery.isError}
                    loadErrorText={t('services.manual_order.load_error')}
                    saveErrorText={t('services.manual_order.save_error')}
                    save={(useCase, ids) => reorderCategories({ useCase, ids }).unwrap()}
                />
            )}
            {isSelfService && (
                <ManualOrderSection
                    id={'manual-order-products'}
                    title={t('services.manual_order.products.title')}
                    description={t('services.manual_order.products.description')}
                    scopeLabel={t('services.manual_order.products.scope')}
                    scopes={productScopes}
                    readOnly={readOnly}
                    isLoading={categoriesQuery.isLoading || productsQuery.isLoading}
                    isError={categoriesQuery.isError || productsQuery.isError}
                    loadErrorText={t('services.manual_order.load_error')}
                    saveErrorText={t('services.manual_order.save_error')}
                    save={(categoryId, ids) => reorderProducts({ categoryId, ids }).unwrap()}
                />
            )}
            <ManualOrderSection
                id={'manual-order-objects'}
                title={t('services.manual_order.objects.title')}
                description={t('services.manual_order.objects.description')}
                scopeLabel={t('services.manual_order.objects.scope')}
                scopes={groupScopes}
                readOnly={readOnly}
                isLoading={groupsQuery.isLoading}
                isError={groupsQuery.isError}
                loadErrorText={t('services.manual_order.load_error')}
                saveErrorText={t('services.manual_order.save_error')}
                save={(locationId, ids) => reorderGroups({ locationId, ids }).unwrap()}
            />
        </div>
    );
};

export default CatalogueOrderSections;
